import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ColDef, RowClickedEvent } from "ag-grid-community";
import { useNavigate } from "react-router-dom";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import { Plus } from "lucide-react";
import {
  usePaymentBanking,
  usePaymentBankingStore,
} from "hooks";
import {
  PaymentBankingQuery,
  PaymentBankingRow,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { ROUTES } from "utils";

const formatDate = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("vi-VN");
};

const compareDate = (filterDate: Date, cellValue?: string) => {
  if (!cellValue) return -1;
  const cellDate = new Date(cellValue);
  if (Number.isNaN(cellDate.getTime())) return 1;
  const cellOnly = new Date(
    cellDate.getFullYear(),
    cellDate.getMonth(),
    cellDate.getDate(),
  );
  if (cellOnly < filterDate) return -1;
  if (cellOnly > filterDate) return 1;
  return 0;
};

const parseNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseId = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeAttachments = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item ?? "")))
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? item : String(item ?? "")))
          .map((item) => item.trim())
          .filter(Boolean);
      }
    } catch {
      // ignore invalid JSON strings
    }

    return trimmed
      .split(/[,\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const sanitizeItems = (items?: PaymentBankingRow["items"]) =>
  Array.isArray(items)
    ? items
        .map((item) => ({
          amount: parseNumber(item.amount),
          partner_id: parseId(item.partner_id),
          expense_reason_id: parseId(item.expense_reason_id),
          description: item.description ? String(item.description).trim() : undefined,
        }))
        .filter(
          (item) =>
            item.amount ||
            item.partner_id ||
            item.expense_reason_id ||
            Boolean(item.description),
        )
    : [];

const withItemFields = (banking: PaymentBankingRow): PaymentBankingRow => {
  const normalizedItems = sanitizeItems(banking.items);
  const firstItem = normalizedItems[0];
  return {
    ...banking,
    bank_account_id: parseId(banking.bank_account_id),
    bank_id: parseId(banking.bank_id),
    import_invoice_id: parseId(banking.import_invoice_id),
    attachments: normalizeAttachments(banking.attachments),
    items: normalizedItems,
    amount:
      banking.amount ??
      (typeof firstItem?.amount === "number"
        ? firstItem.amount
        : firstItem?.amount
          ? Number(firstItem.amount)
          : undefined),
    partner_id: parseId(banking.partner_id ?? firstItem?.partner_id),
    expense_reason_id: parseId(
      banking.expense_reason_id ?? firstItem?.expense_reason_id,
    ),
    item_description: banking.item_description ?? firstItem?.description,
  };
};

const PaymentBankings: React.FC = () => {
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = useState<PaymentBankingQuery>({
    page: 1,
    pageSize: 20,
  });
  const [confirmRow, setConfirmRow] = useState<PaymentBankingRow | null>(null);

  const {
    rows,
    setRows,
    removeRow,
    isLoading,
    setLoading,
  } = usePaymentBankingStore();

  const { fetchList, deletePaymentBanking } = usePaymentBanking();

  const {
    data: paymentBankingData,
    isFetching,
    refetch,
  } = fetchList(queryParams, true);

  useEffect(() => {
    if (paymentBankingData?.data) {
      const mapped =
        (paymentBankingData.data as PaymentBankingRow[])?.map((item) =>
          withItemFields(item),
        ) ?? [];
      setRows(mapped);
    }
  }, [paymentBankingData, setRows]);

  useEffect(() => {
    setLoading(Boolean(isFetching || deletePaymentBanking.isPending));
  }, [deletePaymentBanking.isPending, isFetching, setLoading]);

  const columns = useMemo<ColDef<PaymentBankingRow>[]>(() => {
    return [
      { headerName: "Mã phiếu", field: "payment_code", minWidth: 140, filter: "agTextColumnFilter" },
      {
        headerName: "Ngày chi",
        field: "payment_date",
        minWidth: 140,
        filter: "agDateColumnFilter",
        filterParams: { comparator: compareDate },
        valueFormatter: (params) => formatDate(params.value),
      },
      { headerName: "Tài khoản NH", field: "bank_account_id", minWidth: 140, filter: "agNumberColumnFilter" },
      { headerName: "Ngân hàng", field: "bank_id", minWidth: 140, filter: "agNumberColumnFilter" },
      { headerName: "Người nộp", field: "trader_name", flex: 1, minWidth: 160, filter: "agTextColumnFilter" },
      { headerName: "Người nhận", field: "receiver_name", flex: 1, minWidth: 160, filter: "agTextColumnFilter" },
      { headerName: "Tài khoản nhận", field: "receiver_account", minWidth: 160, filter: "agTextColumnFilter" },
      { headerName: "Ngân hàng nhận", field: "receiver_bank", minWidth: 160, filter: "agTextColumnFilter" },
      { headerName: "Hóa đơn nhập", field: "import_invoice_id", minWidth: 140, filter: "agNumberColumnFilter" },
      {
        headerName: "Tệp đính kèm",
        field: "attachments",
        minWidth: 160,
        filter: "agTextColumnFilter",
        valueFormatter: (params) =>
          Array.isArray(params.value) ? params.value.join(", ") : params.value ?? "",
      },
      {
        headerName: "Số tiền",
        field: "amount",
        minWidth: 140,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN")
            : "",
      },
      { headerName: "Đối tác", field: "partner_id", minWidth: 140, filter: "agNumberColumnFilter" },
      { headerName: "Lý do chi", field: "expense_reason_id", minWidth: 140, filter: "agNumberColumnFilter" },
      { headerName: "Mô tả chi tiết", field: "item_description", flex: 1, minWidth: 200, filter: "agTextColumnFilter" },
      { headerName: "Mô tả", field: "description", flex: 1, minWidth: 200, filter: "agTextColumnFilter" },
    ];
  }, []);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const handleAdd = useCallback(() => {
    navigate(ROUTES.ACCOUNTING.BANK_PAYMENT_CREATE);
  }, [navigate]);

  const handleEditRow = useCallback(
    (params: any) => {
      if (params?.data?.id) {
        navigate(`${ROUTES.ACCOUNTING.BANK_PAYMENT_DETAIL}/${params.data.id}`);
      }
    },
    [navigate],
  );

  const handleRowClick = useCallback(
    (event: RowClickedEvent<PaymentBankingRow>) => {
      if (event?.data?.id) {
        navigate(`${ROUTES.ACCOUNTING.BANK_PAYMENT_DETAIL}/${event.data.id}`);
      }
    },
    [navigate],
  );

  const handleDeleteRow = useCallback((row: PaymentBankingRow) => {
    setConfirmRow(row);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deletePaymentBanking.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        setConfirmRow(null);
        refetch();
      },
      onError: () => {
        SnackbarUtils.error("Xóa phiếu chi ngân hàng không thành công");
      },
    });
  }, [confirmRow, deletePaymentBanking, refetch, removeRow]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      editable: false,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">
          [Kế toán] Phiếu chi ngân hàng
        </h1>
        <p className="text-sm text-gray-600">
          Danh sách phiếu chi ngân hàng. Bấm Thêm hoặc nhấp vào từng dòng để chỉnh sửa trên trang chi tiết.
        </p>
      </div>

      <div className="relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách phiếu chi ngân hàng
            </h2>
            <p className="text-xs text-gray-500">
              Quản lý phiếu chi ngân hàng và chuyển tới trang chi tiết để thêm/cập nhật.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus className="h-4 w-4 text-green-700" />
              Thêm phiếu chi NH
            </button>
          </div>
        </div>

        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          defaultColDef={defaultColDef}
          totalPages={paymentBankingData?.totalPage}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onQueryChange={handleQueryChange}
          onRowClicked={handleRowClick}
          getRowId={(params) => String(params.data.id)}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách phiếu chi ngân hàng, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa phiếu chi ngân hàng?"
        description={`Bạn chắc chắn muốn xóa phiếu chi ngân hàng "${confirmRow?.payment_code ?? ""
          }"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deletePaymentBanking.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default PaymentBankings;
